import { Shot } from "@/app/types/shot-breakdown.types";
import {
  Camera,
  Clock,
  Target,
  Activity,
  Settings2,
  Frame,
  Video,
  AlignLeft,
  Aperture,
  SlidersHorizontal,
  Crosshair,
  Sun,
  Volume2,
  Wand2,
  MonitorPlay,
} from "lucide-react";
import React from "react";

export default function ViewShotModal({ shot }: { shot: Shot }) {
  const PropertyRow = ({ icon: Icon, label, value }: any) => (
    <div className="flex items-start gap-3 py-1.5 group">
      <div className="flex items-center gap-2.5 w-32 shrink-0 text-slate-500 mt-0.5">
        <Icon
          className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors"
          strokeWidth={2}
        />
        <span className="text-[13px] font-medium">{label}</span>
      </div>
      <div className="text-[13px] font-medium text-slate-900 flex-1 break-words whitespace-pre-wrap leading-relaxed">
        {value || (
          <span className="text-slate-400 italic font-normal">Unspecified</span>
        )}
      </div>
    </div>
  );

  const DescriptionBlock = ({ icon: Icon, label, value }: any) => (
    <div className="flex flex-col sm:flex-row gap-4 md:gap-6 mb-12 sm:mb-10 w-full">
      <div className="shrink-0 mt-1 hidden sm:block">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-50 border border-slate-100">
          <Icon className="w-4 h-4 text-[#15607b]/70" />
        </div>
      </div>
      <div className="flex flex-col gap-2.5 flex-1 w-full max-w-2xl">
        <div className="flex items-center gap-2 sm:hidden mb-1">
          <Icon className="w-4 h-4 text-[#15607b]/70" />
          <h3 className="text-[13px] font-bold uppercase tracking-wider text-[#15607b]">
            {label}
          </h3>
        </div>
        <h3 className="hidden sm:block text-[14px] font-semibold text-slate-900">
          {label}
        </h3>
        <div className="text-[15px] leading-[1.8] text-slate-600 font-normal whitespace-pre-wrap">
          {value || (
            <span className="text-slate-400 italic">
              No description provided.
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row bg-white max-h-[85vh] rounded-b-[2.5rem] overflow-y-auto w-full custom-scrollbar relative items-start">
      {/* Main Content Area (Left) */}
      <div className="flex-1 p-6 sm:p-8 md:p-12 w-full min-h-0">
        <div className="max-w-3xl mx-auto lg:mx-0">
          <div className="mb-12 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center justify-center bg-slate-50 text-slate-400 font-mono text-[12px] font-semibold px-2 py-1 rounded-[2.5rem] border border-slate-200/60 shadow-sm">
                SH-{shot.shot_number.toString().padStart(3, "0")}
              </span>
              {shot.shot_type && (
                <div className="flex items-center gap-1.5 bg-blumine-50 border border-blumine-400 px-3 py-1 rounded-[2.5rem] text-blumine-500 shadow-sm">
                  <span className="text-[13px] font-bold uppercase tracking-widest opacity-90">
                    Shot Type:
                  </span>
                  <span className="text-[13px] font-bold tracking-wide">
                    {shot.shot_type}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col mt-2">
              <span className="text-[11px] font-bold uppercase tracking-widest text-blumine-700 mb-2">
                Subject
              </span>
              <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight break-words">
                {shot.subject || "No Subject Defined"}
              </h1>
            </div>
          </div>

          <div className="mt-10">
            <DescriptionBlock
              icon={AlignLeft}
              label="Action & Blocking"
              value={shot.action_blocking}
            />
            <DescriptionBlock
              icon={Target}
              label="Reasoning & Importance"
              value={shot.reason}
            />
            <DescriptionBlock
              icon={Activity}
              label="Emotional Intent"
              value={shot.emotional_purpose}
            />
            <DescriptionBlock
              icon={MonitorPlay}
              label="Editorial Role"
              value={shot.edit_purpose}
            />
          </div>
        </div>
      </div>

      {/* Properties Sidebar (Right) */}
      <div className="w-full lg:w-[340px] xl:w-[380px] shrink-0 bg-[#fdfdfd] border-t lg:border-t-0 lg:border-l border-slate-200/60 p-6 sm:p-8 pb-12 lg:pb-8 h-full">
        <div className="flex flex-col gap-2 mb-8">
          <h4 className="text-[11px] font-bold uppercase tracking-widest text-blumine-700 mb-4 ml-1">
            Core Identity
          </h4>

          <PropertyRow icon={Video} label="Shot Type" value={shot.shot_type} />
          <PropertyRow
            icon={Target}
            label="Priority"
            value={
              <span
                className={`inline-flex items-center rounded-[2.5rem] px-2 py-0.5 text-[11px] font-bold tracking-wide ${
                  shot.priority === "Essential"
                    ? "bg-green-50 text-green-700 border border-green-200/60"
                    : "bg-orange-100 text-orange-600 border border-orange-200/60"
                }`}
              >
                {shot.priority}
              </span>
            }
          />
          <PropertyRow icon={Crosshair} label="Subject" value={shot.subject} />
          <PropertyRow
            icon={Clock}
            label="Duration"
            value={shot.duration_seconds ? `${shot.duration_seconds}` : null}
          />
        </div>

        <div className="w-full h-px bg-slate-100 mb-8 mt-4"></div>

        <div className="flex flex-col sm:flex-row lg:flex-col gap-10 lg:gap-8 w-full">
          <div className="flex-1">
            <h4 className="text-[11px] font-bold uppercase tracking-widest text-[#15607b] mb-5 ml-1">
              Optics & Gear
            </h4>
            <div className="flex flex-col gap-2">
              <PropertyRow
                icon={Camera}
                label="Camera"
                value={shot.camera_name}
              />
              <PropertyRow
                icon={Aperture}
                label="Lens MM"
                value={shot.lens_mm}
              />
              <PropertyRow
                icon={SlidersHorizontal}
                label="Aperture"
                value={shot.aperture}
              />
              <PropertyRow
                icon={Frame}
                label="Support"
                value={shot.camera_support}
              />
              <PropertyRow
                icon={Clock}
                label="Frame Rate"
                value={shot.fps ? `${shot.fps} fps` : null}
              />
              <PropertyRow
                icon={Frame}
                label="Angle"
                value={
                  Array.isArray(shot.camera_angle)
                    ? shot.camera_angle.map((a: any) => a.name).join(", ")
                    : shot.camera_angle
                }
              />
              <PropertyRow
                icon={Frame}
                label="Movement"
                value={shot.camera_movement}
              />
            </div>
          </div>

          <div className="flex-1">
            <h4 className="text-[11px] font-bold uppercase tracking-widest text-[#15607b] mb-5 ml-1">
              Visual Profile
            </h4>
            <div className="flex flex-col gap-2">
              <PropertyRow icon={Frame} label="Framing" value={shot.framing} />
              <PropertyRow
                icon={Aperture}
                label="Depth Field"
                value={shot.depth_of_field}
              />
              <PropertyRow
                icon={Sun}
                label="Lighting"
                value={
                  Array.isArray(shot.lighting)
                    ? shot.lighting.map((l: any) => l.name).join(", ")
                    : shot.lighting
                }
              />
              <PropertyRow icon={Wand2} label="VFX" value={shot.vfx} />
              <PropertyRow
                icon={Volume2}
                label="Sound"
                value={
                  Array.isArray(shot.sound)
                    ? shot.sound.map((s: any) => s.name).join(", ")
                    : shot.sound
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
